import { useMemo } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { GET_NEXT_STEPS } from "../api/queries";
import { graphQLClient } from "../graphqlClient";
import { useReactQueryErrorReporter } from "./use-error-reporter";
import { useCurrentUser } from "./useCurrentUser";
import { useReporting } from "./useReporting";
import { useProductFeatures } from "./useProductFeatures";

// The `instructions` field is an AWSJSON scalar that carries CMS rich-text.
// The CMS publishes it already JSON-stringified, and AppSync's AWSJSON encodes
// it a second time, so it arrives double-encoded. Unwrap the extra layer(s)
// until we reach the underlying rich-text array/object that RichText expects.
const decodeInstructions = (instructions) => {
  let value = instructions;
  while (typeof value === "string") {
    const trimmed = value.trim();
    if (!/^[[{"]/.test(trimmed)) break;
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed === value) break;
      value = parsed;
    } catch {
      break;
    }
  }
  return value;
};

export const useNextSteps = () => {
  const currentUser = useCurrentUser();
  const userPath = currentUser?.userPath;
  const { recentEvents } = useReporting();
  const { productPlans, activeProduct } = useProductFeatures();
  const productCode = activeProduct?.code;

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: [
      "nextSteps",
      recentEvents?.length || 0,
      productPlans,
      productCode,
    ],
    queryFn: () => {
      return graphQLClient.request(GET_NEXT_STEPS, {
        userPath,
        recentEvents,
        productPlans: productPlans || [],
        productCode,
      });
    },
    enabled: !!userPath,
    // The query key includes recentEvents.length, so recording an event (e.g.
    // recommended-cluster-found) spins up a new query. Keep showing the prior
    // data while it refetches so consumers gating on `loading` (the MS
    // dashboard blanks to a full-screen loader) don't flash like a page reload.
    placeholderData: keepPreviousData,
  });

  useReactQueryErrorReporter("Could not load next steps data", error, false);

  const nextSteps = useMemo(
    () =>
      (data?.getNextSteps || []).map((step) => ({
        ...step,
        instructions: decodeInstructions(step.instructions),
      })),
    [data],
  );

  return {
    nextSteps,
    loading: isLoading,
    error,
    refetch,
  };
};
